import React, { forwardRef, ReactNode, useCallback, useEffect, useRef } from "react";

import { faLocationArrow, faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import AutoComplete from "antd/es/auto-complete";
import Input, { InputRef } from "antd/es/input";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";

import { useConfigProvider } from "../../../ConfigProvider";
import LoadingSpinner from "../../../LoadingSpinner";
import ErrorRow from "../../ErrorRow";
import Labeled, { LabeledProps } from "../../Labeled";
import { AddressPickDetails, AddressPresets } from "../utils/AddressPresets";
import useAddressPresets from "../utils/useAddressPresets";
import useGoogleAutoCompleteOptions, {
  MANUAL_ADDRESS_VALUE,
  NO_RESULT_VALUE,
} from "../utils/useGoogleAutoCompleteOptions";
import usePlaceSearch from "../utils/usePlaceSearch";

const block = bem("mmc-auto-complete-address");

export interface AutoCompleteAddressProps extends Omit<LabeledProps, "children"> {
  allowClear?: boolean;
  "aria-label"?: string; // names the input itself, for a host that draws no visible label
  caption?: string;
  className?: string;
  disabled?: boolean;
  disableFindMe?: boolean;
  dropdownMatchSelectWidth?: boolean;
  error?: string;
  onBlur?: () => void;
  onChange?: (address?: Address, details?: AddressPickDetails) => void;
  onEnterManually?: (value: string) => void;
  onPressEnter?: () => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
  placeholder?: string;
  placePredictionTypes?: string[];
  presets?: AddressPresets;
  ref?: React.Ref<InputRef>;
  renderOptionSuffix?: (prediction: google.maps.places.AutocompletePrediction) => ReactNode;
  value?: Address;
}

const AutoCompleteAddress = forwardRef<InputRef, AutoCompleteAddressProps>(
  (
    {
      allowClear,
      "aria-label": ariaLabel,
      caption,
      className,
      disabled,
      disableFindMe,
      dropdownMatchSelectWidth = false,
      error,
      labelClassName,
      onBlur,
      onChange,
      onEnterManually,
      onPressEnter,
      onReverseGeocodeAddress,
      placeholder,
      placePredictionTypes,
      presets,
      renderOptionSuffix,
      required,
      value,
      ...labelProps
    },
    ref
  ) => {
    const { formatMessage, useFindMyLocation } = useConfigProvider();

    const {
      fieldValue,
      placeList,
      placeListLoading,
      placePredictionsGetter,
      selectPlace,
      setFieldValue,
      setFieldValueWithoutSearch,
      setPlacesList,
    } = usePlaceSearch({
      isPrivateQuery: presets?.isPrivateQuery,
      onChange,
      placePredictionTypes,
      value,
    });

    const handleSelect = useCallback(
      (placeId: string) => {
        if ([MANUAL_ADDRESS_VALUE, NO_RESULT_VALUE].includes(placeId)) {
          if (placeId === MANUAL_ADDRESS_VALUE) {
            onEnterManually?.(fieldValue);
          }
          setFieldValue("");
          return;
        }
        const place = placeList?.find(({ place_id }) => place_id === placeId);
        if (place) {
          setFieldValue(place.description);
        }
        selectPlace(placeId, place?.structured_formatting?.main_text);
      },
      [fieldValue, onEnterManually, placeList, selectPlace, setFieldValue]
    );

    const [, , findMeLoading, findMeError, handleFindMyLocation, geocodingResult] =
      useFindMyLocation(onReverseGeocodeAddress);
    const lastGeocodingResult = useRef<GeocodeResult>();
    useEffect(() => {
      if (lastGeocodingResult.current !== geocodingResult) {
        lastGeocodingResult.current = geocodingResult;
        // the resolved address is not a search result, so drop back to the un-searched state
        setPlacesList(undefined);
        onChange?.(geocodingResult?.address);
      }
    }, [geocodingResult, onChange, setPlacesList]);

    const options = useGoogleAutoCompleteOptions(placeList, !!onEnterManually, renderOptionSuffix);

    const checkTextAndFindPlaceIfNeeded = useCallback(async () => {
      if (getFormattedAddressForUi(value) !== fieldValue) {
        const places = await placePredictionsGetter(fieldValue);
        if (places?.length) {
          selectPlace(places[0].place_id, places[0].structured_formatting?.main_text);
        } else if (!places) {
          setFieldValueWithoutSearch(getFormattedAddressForUi(value));
        } else if (allowClear) {
          onChange?.(undefined);
        }
      }
    }, [
      allowClear,
      fieldValue,
      placePredictionsGetter,
      onChange,
      selectPlace,
      setFieldValueWithoutSearch,
      value,
    ]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Escape") {
          setFieldValueWithoutSearch(getFormattedAddressForUi(value));
        } else if (event.key === "Enter") {
          checkTextAndFindPlaceIfNeeded();
        }
      },
      [checkTextAndFindPlaceIfNeeded, setFieldValueWithoutSearch, value]
    );

    const handleClear = useCallback(() => {
      onChange?.(undefined);
      setFieldValueWithoutSearch("");
    }, [onChange, setFieldValueWithoutSearch]);

    const presetProps = useAddressPresets({
      fieldValue,
      onBlur,
      onChange,
      onKeyDown: handleKeyDown,
      onSelect: handleSelect,
      onTextBlur: checkTextAndFindPlaceIfNeeded,
      options,
      presets,
      setFieldValue,
      setFieldValueWithoutSearch,
      setPlacesList,
      value,
    });

    return (
      <Labeled
        labelClassName={cn(block("label"), labelClassName)}
        required={required}
        {...labelProps}
      >
        <AutoComplete
          className={cn(block(), className)}
          disabled={disabled}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          onBlur={disabled ? undefined : presetProps.onBlur}
          onChange={disabled ? undefined : presetProps.onChange}
          onClear={disabled ? undefined : handleClear}
          onDropdownVisibleChange={presetProps.onDropdownVisibleChange}
          onFocus={disabled ? undefined : presetProps.onFocus}
          onKeyDown={disabled ? undefined : presetProps.onKeyDown}
          onSelect={disabled ? undefined : presetProps.onSelect}
          open={presetProps.open}
          options={presetProps.options}
          showAction={presetProps.showAction}
          showSearch={!disabled}
          value={fieldValue}
        >
          <Input
            allowClear={allowClear && !disabled}
            aria-label={ariaLabel}
            disabled={disabled}
            onPressEnter={onPressEnter}
            placeholder={
              placeholder ??
              (disabled ? undefined : formatMessage("ui.address.autoCompleteAddress.placeholder"))
            }
            ref={ref}
            size="large"
            suffix={
              findMeLoading || placeListLoading ? (
                <LoadingSpinner mini />
              ) : disabled ? (
                <FontAwesomeIcon className={block("lock")} icon={faLock} size="sm" />
              ) : disableFindMe ? (
                // an empty div, not nothing: suffix copes badly with markup changing to null
                <div />
              ) : (
                <Tooltip title={formatMessage("ui.address.findMe.tooltip")}>
                  <FontAwesomeIcon
                    className={block("icon")}
                    icon={faLocationArrow}
                    onClick={handleFindMyLocation}
                    size="lg"
                  />
                </Tooltip>
              )
            }
          />
        </AutoComplete>
        {findMeError || error ? (
          <ErrorRow>{findMeError ? findMeError : error}</ErrorRow>
        ) : caption ? (
          <div className={block("caption")}>{caption}</div>
        ) : null}
      </Labeled>
    );
  }
);

AutoCompleteAddress.displayName = "AutoCompleteAddress";

export default AutoCompleteAddress;
